import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  ChartLineUpIcon, BroadcastIcon, CpuIcon, FloppyDiskIcon, UserCircleIcon, XIcon,
  ArrowRightIcon, ArrowLeftIcon, DeviceMobileIcon, ShieldCheckIcon, StarIcon, ListIcon,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const KEY = "alphaapeing_tour_done_v1";
const NEON = "#00E599";
const CYAN = "#22D3EE";
const GOLD = "#F0C000";

const STEPS = [
  {
    icon: ChartLineUpIcon, color: NEON, tag: "WELCOME",
    title: "Welcome to AlphaApeing",
    body: "Your intelligence terminal for Solana & Robinhood meme coins. We surface the coins worth watching and the wallets worth copying — so you're early, not exit liquidity. Quick tour?",
  },
  {
    icon: ListIcon, color: NEON, tag: "GET AROUND",
    title: "Everything lives in the sidebar",
    body: "Every page is one click away here — Radar, Smart Money, Cluster Buys, Alerts and more. Hover any link for a one-line explainer. Collapse it anytime with the arrow at the bottom.",
    target: '[data-testid="app-sidebar"]', targetAlt: '[data-testid="mobile-topbar"]',
  },
  {
    icon: ChartLineUpIcon, color: NEON, tag: "THE RADAR",
    title: "Live coins, ranked by Degen Score",
    body: "This is your main board — every live coin scored 0–100 on volume, liquidity, buy pressure, survival & credibility. One-tap filters (Quality, Conviction, LP-safe) cut straight to the runners.",
    target: '[data-testid="radar-section"]',
  },
  {
    icon: BroadcastIcon, color: GOLD, tag: "SMART MONEY",
    title: "Command Center — winners, live",
    body: "Track proven wallets and watch their buys & sells stream in real time, plus Cluster Buys and Milly's Call when smart money loads up. Turn on the bell for instant pings.",
    target: '[data-testid="command-center"]',
  },
  {
    icon: CpuIcon, color: CYAN, tag: "TECH TOKENS",
    title: "Real products, not just memes",
    body: "Coins that ship a real website, socials and utility — we live-check each site (Live / Parked / Dead) so you can spot product plays that grind up instead of dumping.",
    target: '[data-testid="tech-tokens"]',
  },
  {
    icon: FloppyDiskIcon, color: NEON, tag: "SAVE YOUR SETUP", signup: true,
    title: "Create a free account — keep everything",
    body: "Your tracked wallets, Alpha devs, watchlist and alerts only stick around if you have an account. Sign up free to save them and sync across every device — no account means it all resets when you leave.",
    perks: [
      { icon: StarIcon, text: "Watchlist, tracked wallets & creators saved forever" },
      { icon: DeviceMobileIcon, text: "Sync across phone, laptop & desktop" },
      { icon: ShieldCheckIcon, text: "Telegram alerts tied to your account" },
    ],
  },
];

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export const WelcomeTour = () => {
  const { isAuthed, openAuth } = useAuth();
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [cardH, setCardH] = useState(320);
  const cardRef = useRef(null);

  useEffect(() => {
    if (!isAuthed && localStorage.getItem(KEY) !== "1") {
      const t = setTimeout(() => setOpen(true), 900);
      return () => clearTimeout(t);
    }
  }, [isAuthed]);

  const s = STEPS[step];

  const measure = useCallback(() => {
    if (!s?.target) { setRect(null); return; }
    let el = document.querySelector(s.target);
    let r = el && el.getBoundingClientRect();
    if ((!el || (r.width === 0 && r.height === 0)) && s.targetAlt) {
      el = document.querySelector(s.targetAlt);
      r = el && el.getBoundingClientRect();
    }
    if (!el || (r.width === 0 && r.height === 0)) { setRect(null); return; }
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [s]);

  const resolveEl = useCallback(() => {
    if (!s?.target) return null;
    let el = document.querySelector(s.target);
    if (el && el.getBoundingClientRect().height === 0 && s.targetAlt) el = document.querySelector(s.targetAlt);
    return el;
  }, [s]);

  // On step change: scroll target into view, then measure.
  useLayoutEffect(() => {
    if (!open) return;
    if (!s?.target) { setRect(null); return; }
    const el = resolveEl();
    if (el) {
      try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { el.scrollIntoView(); }
    }
    const t1 = setTimeout(measure, 300);
    const t2 = setTimeout(measure, 650);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [open, step, s, measure, resolveEl]);

  // Keep the spotlight glued to the element on scroll/resize.
  useEffect(() => {
    if (!open || !s?.target) return;
    const onMove = () => measure();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => { window.removeEventListener("scroll", onMove, true); window.removeEventListener("resize", onMove); };
  }, [open, s, measure]);

  useLayoutEffect(() => {
    if (cardRef.current) setCardH(cardRef.current.offsetHeight);
  }, [step, open, rect]);

  if (!open) return null;

  const finish = () => { localStorage.setItem(KEY, "1"); setOpen(false); };
  const createAccount = () => {
    localStorage.setItem(KEY, "1");
    setOpen(false);
    openAuth("signup", "", "save your wallets, watchlist & alerts");
  };

  const Icon = s.icon;
  const last = step === STEPS.length - 1;

  // Spotlight geometry (clamped so tall sections don't fill the screen).
  let spot = null;
  if (rect) {
    const pad = 8;
    const maxH = window.innerHeight * 0.6;
    const height = Math.min(rect.height + pad * 2, maxH);
    spot = { top: rect.top - pad, left: Math.max(rect.left - pad, 4), width: Math.min(rect.width + pad * 2, window.innerWidth - 8), height };
  }

  // Card placement.
  const cardW = Math.min(380, window.innerWidth - 24);
  let cardStyle;
  if (spot) {
    const gap = 14;
    const spotBottom = spot.top + spot.height;
    let top;
    if (spotBottom + gap + cardH < window.innerHeight) top = spotBottom + gap;
    else if (spot.top - gap - cardH > 8) top = spot.top - gap - cardH;
    else top = clamp((window.innerHeight - cardH) / 2, 12, window.innerHeight - cardH - 12);
    const left = clamp(spot.left, 12, Math.max(12, window.innerWidth - cardW - 12));
    cardStyle = { position: "fixed", top, left, width: cardW };
  } else {
    cardStyle = { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: cardW };
  }

  return createPortal(
    <div className="fixed inset-0 z-[100]" data-testid="welcome-tour">
      {/* click blocker */}
      <div className="absolute inset-0" style={{ background: spot ? "transparent" : "rgba(0,0,0,0.82)", backdropFilter: spot ? undefined : "blur(2px)" }} onClick={(e) => e.stopPropagation()} />
      {/* spotlight cutout */}
      {spot && (
        <div
          data-testid="tour-spotlight"
          className="pointer-events-none"
          style={{
            position: "fixed", top: spot.top, left: spot.left, width: spot.width, height: spot.height,
            borderRadius: 10, border: `2px solid ${s.color}`, boxShadow: `0 0 0 9999px rgba(0,0,0,0.82), 0 0 22px ${s.color}66`,
            transition: "all 0.25s ease",
          }}
        />
      )}

      {/* card */}
      <div ref={cardRef} style={cardStyle} className="bg-[#0a0d11] border border-white/15 shadow-2xl shadow-black/60 animate-in fade-in-0 zoom-in-95 duration-200">
        <button data-testid="tour-skip" onClick={finish} title="Skip" className="absolute top-3 right-3 z-10 p-1.5 text-muted-foreground hover:text-white transition-colors">
          <XIcon size={18} />
        </button>

        <div className="p-6 pt-6">
          <div className="w-12 h-12 flex items-center justify-center border mb-4" style={{ borderColor: `${s.color}55`, color: s.color, background: `${s.color}12` }}>
            <Icon size={26} weight="fill" />
          </div>
          <div className="font-mono text-[10px] tracking-[0.25em] font-bold mb-1.5" style={{ color: s.color }}>{s.tag}</div>
          <h2 className="font-heading font-black text-xl tracking-tighter leading-tight mb-2.5">{s.title}</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>

          {s.perks && (
            <div className="mt-4 space-y-2.5 border-t border-white/10 pt-4">
              {s.perks.map((p, i) => {
                const PIcon = p.icon;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <PIcon size={18} weight="fill" style={{ color: NEON }} className="shrink-0" />
                    <span className="text-sm text-white/90">{p.text}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-center gap-1.5 pb-3.5">
          {STEPS.map((_, i) => (
            <span key={i} className="h-1.5 rounded-full transition-all duration-200" style={{ width: i === step ? 22 : 6, background: i === step ? s.color : "rgba(255,255,255,0.2)" }} />
          ))}
        </div>

        <div className="flex items-center gap-2 border-t border-white/10 p-3.5">
          {step > 0 ? (
            <button data-testid="tour-back" onClick={() => setStep((v) => v - 1)} className="flex items-center gap-1.5 px-3.5 py-2.5 border border-white/15 text-muted-foreground hover:text-white hover:border-white/30 transition-colors text-sm font-semibold">
              <ArrowLeftIcon size={16} /> Back
            </button>
          ) : (
            <button data-testid="tour-skip-bottom" onClick={finish} className="px-3.5 py-2.5 text-muted-foreground hover:text-white transition-colors text-sm">
              Skip tour
            </button>
          )}
          <div className="flex-1" />
          {last ? (
            <div className="flex items-center gap-1.5">
              <button data-testid="tour-explore" onClick={finish} className="px-3 py-2.5 text-muted-foreground hover:text-white transition-colors text-sm">
                Explore first
              </button>
              <button data-testid="tour-create-account" onClick={createAccount} className="flex items-center gap-2 px-4 py-2.5 bg-neon text-black font-bold text-sm hover:bg-neon/90 transition-colors">
                <UserCircleIcon size={18} weight="fill" /> Create free account
              </button>
            </div>
          ) : (
            <button data-testid="tour-next" onClick={() => setStep((v) => v + 1)} className="flex items-center gap-2 px-5 py-2.5 bg-neon text-black font-bold text-sm hover:bg-neon/90 transition-colors">
              Next <ArrowRightIcon size={16} weight="bold" />
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
